import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { format } from "date-fns";
import { Loader2, ClipboardList } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { AssessmentRecord, OrganizationDetails } from "@/types/assessment";
import { Database } from "@/integrations/supabase/types";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

type SupabaseAssessment = Database['public']['Tables']['assessments']['Row'];

const MasterAssessmentHistory = () => {
  const { toast } = useToast();

  const { data: assessments, isLoading } = useQuery({
    queryKey: ["all-assessments"],
    queryFn: async () => {
      console.log("Fetching all assessments...");
      const { data, error } = await supabase
        .from("assessments")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching assessments:", error);
        toast({
          title: "Error",
          description: "Failed to load assessment history",
          variant: "destructive",
        });
        throw error;
      }

      console.log("Fetched all assessments:", data);
      
      return data.map((assessment: any): AssessmentRecord => ({
        id: assessment.id,
        organization_details: assessment.organization_details as OrganizationDetails,
        answers: assessment.answers as AssessmentRecord['answers'],
        maturity_level: assessment.maturity_level,
        overall_score: assessment.overall_score,
        dimension_scores: assessment.dimension_scores as Record<string, number>,
        description: assessment.description,
        created_at: assessment.created_at || undefined,
        created_by: assessment.created_by
      }));
    },
  });

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!assessments || assessments.length === 0) {
    return (
      <div className="container mx-auto py-8">
        <h1 className="text-2xl font-bold mb-6">Master Assessment History</h1>
        <div className="flex flex-col items-center justify-center py-12 px-4">
          <ClipboardList className="h-16 w-16 text-gray-400 mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 mb-2">No Assessments Found</h2>
          <p className="text-gray-500 text-center">
            There are no completed assessments in the database yet.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8">
      <h1 className="text-2xl font-bold mb-6">Master Assessment History</h1>

      <Card className="w-full">
        <CardHeader>
          <CardTitle>All Assessments</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Organization</TableHead>
                <TableHead>Assessor</TableHead>
                <TableHead>Maturity Level</TableHead>
                <TableHead>Score</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {assessments.map((assessment) => (
                <TableRow key={assessment.id}>
                  <TableCell>
                    {assessment.created_at
                      ? format(new Date(assessment.created_at), "PPP")
                      : "N/A"}
                  </TableCell>
                  <TableCell>{assessment.organization_details.name}</TableCell>
                  <TableCell>{assessment.created_by}</TableCell>
                  <TableCell>{assessment.maturity_level}</TableCell>
                  <TableCell>{assessment.overall_score.toFixed(2)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default MasterAssessmentHistory;
